import { memo, useEffect, useRef, useState } from 'react';
import { formatMoney } from '../format';

export interface WinBarProps {
  win?: number;
  currency?: string;
  duration?: number;
  /** Decimal places shown during / after the count-up. Default: 2. */
  decimals?: number;
}

// Memoized — SlotUI re-renders its whole chrome tree on every swipe-drag
// frame (reelDrag/nextLoading state), and WinBar's own props (win,
// currency, duration, decimals) don't change on those frames. Without
// this, the count-up's internal state churn was the least of it: the
// entire component (plus its own effect setup) would re-run for no reason
// on every frame of an unrelated gesture.
export const WinBar = memo(function WinBar({
  win = 0,
  currency = '$',
  duration = 900,
  decimals = 2,
}: WinBarProps) {
  const [display, setDisplay] = useState(win);
  // The `<span>` showing the number — mutated directly during the rAF
  // loop below instead of going through `setDisplay` every tick. A React
  // state update per animation frame (previously here) competes with
  // whatever the hosted game iframe is doing on the main thread;
  // SpinButton's long-press ring and ReelsStack's drag offset use the
  // same direct-DOM-mutation technique for the same reason.
  const valueRef = useRef<HTMLSpanElement | null>(null);
  // Mirrors what's actually painted on screen at all times, including the
  // ticks where we bypass React state — so a new `win` arriving mid-count
  // resumes the easing from wherever the number visually is right now,
  // not from whatever `display` was last committed to (which only
  // updates at animation start/end).
  const currentRef = useRef(win);
  const fromRef = useRef(win);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  // Tracks the previous non-zero `win` so we can tell "appearing from
  // hidden" (prev was 0, reset count-up to start from 0 for impact)
  // apart from "chained win update" (prev was non-zero, continue the
  // animation from whatever's currently on screen).
  const prevWinRef = useRef(win);
  // True while the count-up rAF is running OR the pop-in/fade-out
  // transition is still resolving. Used to scope `will-change` to the
  // window the layer actually moves in — permanent willChange keeps a
  // compositor surface allocated even at rest, which iOS budgets tightly.
  const [animating, setAnimating] = useState(false);

  // Writes a value straight to the span's text node and keeps `currentRef`
  // in sync. React never sees these intermediate values, so it won't
  // re-render — the final frame (see `step` below) is the only one that
  // also calls `setDisplay`, committing real state.
  const paintValue = (v: number) => {
    currentRef.current = v;
    const el = valueRef.current;
    if (el) el.textContent = formatMoney(v, currency, { decimals });
  };

  useEffect(() => {
    const prev = prevWinRef.current;
    prevWinRef.current = win;

    // When `win` transitions to 0, don't animate down to zero — that
    // flashes "0.00$" during the fade-out. Instead leave `display`
    // frozen at the previous value and let the wrapper opacity hide
    // the pill. We still mark `animating` so the fade-out transition
    // gets willChange while it's running.
    if (win === 0) {
      setAnimating(true);
      return;
    }

    // Fresh appearance: start the count-up from 0 so the roll-in reads
    // as "won X" rather than blending continuously out of the prior
    // balance (which might not even be visible anymore if the pill was
    // hidden when the new win arrived).
    const from = prev === 0 ? 0 : currentRef.current;
    fromRef.current = from;
    if (from === win) {
      setDisplay(win);
      paintValue(win);
      return;
    }
    // Seed the render so the first paint of the fade-in doesn't briefly
    // show the previous (frozen) number before RAF starts interpolating.
    paintValue(from);
    if (from !== display) setDisplay(from);

    startRef.current = null;
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    setAnimating(true);
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (ts: number) => {
      if (startRef.current == null) startRef.current = ts;
      const t = Math.min(1, (ts - startRef.current) / duration);
      const v = fromRef.current + (win - fromRef.current) * ease(t);
      if (t === 1) {
        // Final frame — commit to real state so `display` (used for the
        // initial render / any unrelated re-render) reflects the truth.
        setDisplay(win);
        paintValue(win);
      } else {
        paintValue(v);
      }
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // display is intentionally excluded — we seed via `prev` / the ref
    // and animate to `win`. Re-running on every display tick would
    // restart the easing and stutter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, duration]);

  const shown = formatMoney(display, currency, { decimals });
  const visible = win > 0;
  // Clear `animating` once the wrapper's pop/fade transition resolves so
  // willChange reverts to 'auto' at rest. End-of-count-up is handled by
  // the rAF setting display === win on its last frame.
  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    setAnimating(false);
  };
  return (
    <div
      aria-hidden={!visible}
      onTransitionEnd={handleTransitionEnd}
      style={{
        textAlign: 'center',
        opacity: visible ? 1 : 0,
        // Small scale + vertical nudge gives the pill a "pop" on enter
        // and a gentle recede on exit, keeping the motion language
        // consistent with SpinButton's bounce.
        transform: visible
          ? 'translateY(0) scale(1)'
          : 'translateY(6px) scale(0.92)',
        transition:
          'opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
        pointerEvents: visible ? 'auto' : 'none',
        willChange: animating ? 'opacity, transform' : 'auto',
      }}
    >
      <div
        style={{
          // Inline-block pill that hugs only the "win $X" text — not the
          // full row width. Uses the same #00000088 as the balance strip
          // so the chrome reads as one palette.
          display: 'inline-block',
          padding: '2px 12px',
          borderRadius: 999,
          background: '#00000088',
          fontSize: 14.4,
          color: 'var(--text-muted)',
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        win{' '}
        <span
          ref={valueRef}
          style={{
            color: 'var(--accent)',
            fontWeight: 400,
            fontFamily: 'Oswald, sans-serif',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {shown}
        </span>
      </div>
    </div>
  );
});
